import { useSetProductPricesMutation } from '@codezo/graphql';
import { useState } from 'react';
import { useCurrencyStore } from '../../stores/currency';
import { buildTierGroups, type TierDraft, type TierGroup } from './product-price-tiers';

/**
 * Holds catalogue cost ladders while a product is being created.
 *
 * Prices cannot ride along on `createProduct` — they are rows of their own,
 * keyed to a product that does not exist yet — so they are staged here and
 * written once the new id comes back, the way staged images are attached.
 *
 * Tiers may span SEVERAL ladders: each one names its own supplier, and they are
 * grouped into one `setProductPrices` call per ladder, since the mutation
 * replaces one supplier's ladder at a time. Safe here in a way it would not be
 * in the edit modal — the product is new, so no call can delete rows that a
 * previous one wrote.
 */
export function useStagedProductPrices() {
	const { selectedCurrency } = useCurrencyStore();
	const [setProductPrices] = useSetProductPricesMutation();
	const [tiers, setTiers] = useState<TierDraft[]>([]);
	const [error, setError] = useState<string | null>(null);

	const reset = () => {
		setTiers([]);
		setError(null);
	};

	/**
	 * Checks the staged tiers before anything is created — a rejected tier should
	 * cost a correction, not leave a product behind that has to be cleaned up.
	 * Returns null when they don't validate, having set `error`.
	 */
	const validate = (): TierGroup[] | null => {
		const built = buildTierGroups(tiers);
		if (!built.ok) {
			setError(built.error);
			return null;
		}
		setError(null);
		return built.groups;
	};

	/**
	 * Writes the ladders against the new product. Returns an error message rather
	 * than throwing: the product exists by this point, so a failure is reported
	 * without discarding it.
	 *
	 * Sequential, not parallel — each call is a whole-ladder replace, and one
	 * failing should not leave the rest racing against it.
	 */
	const write = async (productId: string, groups: TierGroup[] | null) => {
		if (!groups || groups.length === 0) return null;
		for (const group of groups) {
			try {
				await setProductPrices({
					variables: {
						input: {
							productId,
							vetterEntityId: group.vetterEntityId,
							tiers: group.tiers,
						},
					},
				});
			} catch (err) {
				return err instanceof Error ? err.message : 'Prices could not be saved';
			}
		}
		return null;
	};

	return {
		tiers,
		setTiers: (next: TierDraft[]) => {
			setTiers(next);
			setError(null);
		},
		error,
		currency: selectedCurrency,
		reset,
		validate,
		write,
	};
}
